/**
 * Kacheln der aktiven Apartments für die nächsten 14 Tage; Apartments ohne Automatik einklappbar
 * darunter. Die Strategie kommt aus den Profilen (Join über `strategyProfileVersionId`).
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DynamicPricingApartmentOverview } from '@/shared/api';
import { problemText, useApi, useApiProblem } from '@/shared/api';
import { Button } from '@/shared/components';
import { OverviewTile } from './OverviewTile';
import { horizonLabel, shortTime } from './overviewFormat';

export function OverviewApartments({
  apartments,
  halted,
}: {
  apartments: readonly DynamicPricingApartmentOverview[];
  halted: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const profiles = useQuery({
    queryKey: ['pricing-strategy-profiles'],
    queryFn: () => api.pricingStrategyProfiles.list(),
    staleTime: 300_000,
  });
  const problem = useApiProblem(profiles.error);
  const strategy = (apartment: DynamicPricingApartmentOverview) => {
    if (!apartment.strategyProfileVersionId) return 'keine Strategie';
    const profile = profiles.data?.find(
      (entry) => entry.versionId === apartment.strategyProfileVersionId,
    );
    if (profile)
      return String(t(`admin.dynamicPricing.configuration.strategies.${profile.strategy}`));
    if (profiles.isError) return 'Strategie nicht geladen';
    // Die Liste nennt nur aktuelle Fassungen; eine ältere bleibt am Apartment hängen.
    return profiles.isSuccess ? 'Strategie (ältere Fassung)' : 'Strategie wird geladen';
  };
  const active = apartments.filter((apartment) => apartment.mode !== 'Off');
  const inactive = apartments.filter((apartment) => apartment.mode === 'Off');
  const tile = (apartment: DynamicPricingApartmentOverview) => (
    <OverviewTile
      key={apartment.propertyId}
      apartment={apartment}
      strategy={strategy(apartment)}
      halted={halted && apartment.mode !== 'Off'}
      time={shortTime}
      range={horizonLabel(apartment.cockpit.horizonStart, apartment.cockpit.horizonEnd)}
    />
  );
  return (
    <section aria-labelledby="pricing-apartments" className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 id="pricing-apartments" className="grow font-serif text-xl font-bold">
          Apartments · nächste 14 Tage
        </h2>
        <span className="text-ink-soft flex items-center gap-3.5 text-xs">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="border-forest w-[18px] border-t-[3px]" /> bestätigter Preis
          </span>
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="border-amber w-[18px] border-t-2 border-dashed" /> Ziel der
            Automatik
          </span>
        </span>
      </div>
      {profiles.isError && (
        <p
          role="alert"
          className="bg-amber-tint text-amber-ink rounded-tile flex flex-wrap items-center gap-2 px-4 py-2 text-[13px] font-semibold"
        >
          <span className="grow">
            {problemText(problem, 'Die Strategien konnten nicht geladen werden.')}
          </span>
          <Button
            variant="ghost"
            className="min-h-11"
            loading={profiles.isFetching}
            onClick={() => void profiles.refetch()}
          >
            Erneut laden
          </Button>
        </p>
      )}
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
        {active.map(tile)}
      </div>
      {inactive.length > 0 && (
        <details className="bg-card border-hairline rounded-tile border px-5 py-3">
          <summary className="min-h-11 cursor-pointer content-center text-sm font-bold">
            {inactive.length === 1
              ? '1 Apartment ohne Automatik'
              : `${inactive.length} Apartments ohne Automatik`}
          </summary>
          <div className="grid gap-3.5 pb-2 pt-3 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
            {inactive.map(tile)}
          </div>
        </details>
      )}
    </section>
  );
}
